import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../constants/common'
import { isValueStringType } from '../../../utils/common'
import { toDocument } from '../../../utils/html'

import {
  EMAIL_BLOCK_CHILD_TAGS,
  EMAIL_BLOCK_CHILD_TAGS_SELECTOR,
  EMAIL_PREFORMATTED_TAG_NAME,
  EMAIL_TABLE_BORDER_COLOR,
  EMAIL_TABLE_FORBIDDEN_STYLES,
  EMAIL_TABLE_SAFE_BORDER_STYLES,
  EMAIL_UNWRAPPED_BLOCK_TAGS_PATTERN,
  EMAIL_UNWRAPPED_BLOCK_TAGS_SELECTOR,
} from './EmailCreationForm.constants'

function parseInlineStyleAttribute(style) {
  if (!style) {
    return EMPTY_OBJECT
  }

  return style
    .split(';')
    .map(item => item.trim())
    .filter(Boolean)
    .reduce((styles, pair) => {
      const index = pair.indexOf(':')

      if (index !== -1) {
        const key = pair.slice(0, index).trim().toLowerCase()
        const value = pair.slice(index + 1).trim()

        if (key) {
          styles[key] = value
        }
      }

      return styles
    }, {})
}

function serializeInlineStyleAttribute(style) {
  return Object.entries(style)
    .filter(([, value]) => value != null && value !== EMPTY_STRING)
    .map(([key, value]) => `${key}: ${value}`)
    .join('; ')
}

function getEmailSafeStyles(style) {
  return Object.entries(style).reduce((acc, [key, value]) => {
    if (!EMAIL_TABLE_FORBIDDEN_STYLES.includes(key)) {
      acc[key] = value
    }

    return acc
  }, {})
}

function normalizeEmailTableCellSize(value) {
  if (value) {
    if (/%$/.test(value)) {
      const rounded = Math.round(parseFloat(value))

      if (Number.isFinite(rounded)) {
        return `${rounded}%`
      }

      return null
    }

    if (/px$/.test(value)) {
      return value
    }
  }

  return null
}

function normalizeEmailTableElement(table) {
  table.setAttribute('cellpadding', '0')
  table.setAttribute('cellspacing', '0')
  table.setAttribute('border', '0')

  const tableStyle = parseInlineStyleAttribute(table.getAttribute('style'))

  table.setAttribute(
    'style',
    serializeInlineStyleAttribute({
      ...tableStyle,
      'border-collapse': 'collapse',
      ...(tableStyle.width ? EMPTY_OBJECT : { width: '100%' }),
    })
  )
}

function normalizeEmailTableCaption(table) {
  const caption = table.querySelector('caption')

  if (caption) {
    const captionStyle = parseInlineStyleAttribute(
      caption.getAttribute('style')
    )

    caption.setAttribute('align', 'center')
    caption.setAttribute(
      'style',
      serializeInlineStyleAttribute({
        ...captionStyle,
        'text-align': 'center',
        width: '100%',
      })
    )
  }
}

function isSafeBorderStyle(value) {
  return EMAIL_TABLE_SAFE_BORDER_STYLES.includes(value)
}

function normalizeEmailCellBorder({
  cellStyle,
  tableBorderColor,
  tableBorderStyle,
}) {
  if (cellStyle.border) {
    return cellStyle.border
  }

  const borderWidth = cellStyle['border-width']
  const rawBorderStyle = cellStyle['border-style']
  const borderColor = cellStyle['border-color']

  const hasAnyParts = borderWidth || rawBorderStyle || borderColor

  if (!hasAnyParts) {
    return `1px ${tableBorderStyle} ${tableBorderColor}`
  }

  const safeStyle = isSafeBorderStyle(rawBorderStyle)
    ? rawBorderStyle
    : tableBorderStyle

  return `${borderWidth || '1px'} ${safeStyle} ${borderColor || tableBorderColor}`
}

function normalizeEmailCellPadding(cellStyle) {
  const padding = cellStyle.padding

  const paddingTop = cellStyle['padding-top']
  const paddingRight = cellStyle['padding-right']
  const paddingBottom = cellStyle['padding-bottom']
  const paddingLeft = cellStyle['padding-left']

  return {
    padding: padding || '8px 10px',
    ...(paddingTop ? { 'padding-top': paddingTop } : EMPTY_OBJECT),
    ...(paddingRight ? { 'padding-right': paddingRight } : EMPTY_OBJECT),
    ...(paddingBottom ? { 'padding-bottom': paddingBottom } : EMPTY_OBJECT),
    ...(paddingLeft ? { 'padding-left': paddingLeft } : EMPTY_OBJECT),
  }
}

function normalizeEmailTableCells(table) {
  const tableStyle = parseInlineStyleAttribute(table.getAttribute('style'))

  const tableBorderColor =
    tableStyle['border-color'] || EMAIL_TABLE_BORDER_COLOR

  const rawBorderStyle = tableStyle['border-style']
  const tableBorderStyle = isSafeBorderStyle(rawBorderStyle)
    ? rawBorderStyle
    : 'solid'

  table.querySelectorAll('th, td').forEach(cell => {
    const cellStyle = parseInlineStyleAttribute(cell.getAttribute('style'))

    const width = normalizeEmailTableCellSize(cellStyle.width)

    const height = normalizeEmailTableCellSize(cellStyle.height)

    const nextStyle = {
      border: normalizeEmailCellBorder({
        cellStyle,
        tableBorderColor,
        tableBorderStyle,
      }),

      ...normalizeEmailCellPadding(cellStyle),

      'text-align': cellStyle['text-align'] || 'left',
      'vertical-align': cellStyle['vertical-align'] || 'top',

      ...(cellStyle['background-color']
        ? { 'background-color': cellStyle['background-color'] }
        : EMPTY_OBJECT),

      ...(width ? { width } : EMPTY_OBJECT),
      ...(height ? { height } : EMPTY_OBJECT),

      // Preserve safe text-related styles
      ...(cellStyle.color ? { color: cellStyle.color } : EMPTY_OBJECT),
      ...(cellStyle['font-size']
        ? { 'font-size': cellStyle['font-size'] }
        : EMPTY_OBJECT),
      ...(cellStyle['font-weight']
        ? { 'font-weight': cellStyle['font-weight'] }
        : EMPTY_OBJECT),
      ...(cellStyle['font-style']
        ? { 'font-style': cellStyle['font-style'] }
        : EMPTY_OBJECT),
      ...(cellStyle['font-family']
        ? { 'font-family': cellStyle['font-family'] }
        : EMPTY_OBJECT),
    }

    const safeStyle = getEmailSafeStyles(nextStyle)

    cell.setAttribute('style', serializeInlineStyleAttribute(safeStyle))

    const hasText = Boolean(cell.textContent && cell.textContent.trim())
    const hasElements = Boolean(cell.querySelector('*'))

    if (!hasText && !hasElements) {
      cell.innerHTML = '&nbsp;'
    }
  })
}

function withEmailBlockMargin(element, extraStyle = EMPTY_OBJECT) {
  const style = parseInlineStyleAttribute(element.getAttribute('style'))

  element.setAttribute(
    'style',
    serializeInlineStyleAttribute({ margin: '0px', ...style, ...extraStyle })
  )

  return element
}

// A direct block child or an inline wrapper (e.g. <span>) holding one deeper
// down both count: either way, this element isn't a leaf.
function hasEmailBlockChild(element) {
  return Boolean(element.querySelector(EMAIL_BLOCK_CHILD_TAGS_SELECTOR))
}

function isEmailBlockChildNode(node) {
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return false
  }

  return (
    EMAIL_BLOCK_CHILD_TAGS.includes(node.tagName.toLowerCase()) ||
    Boolean(node.querySelector(EMAIL_BLOCK_CHILD_TAGS_SELECTOR))
  )
}

function isWhitespaceOnlyTextNode(node) {
  return node.nodeType === Node.TEXT_NODE && !node.textContent.trim()
}

// No block child: the backend would unwrap it and lose every line break inside.
// <pre> additionally needs its whitespace preserved — a plain <p> would collapse it.
function normalizeEmailLeafBlock(doc, element) {
  const paragraph = doc.createElement('p')
  const isPreformatted =
    element.tagName.toLowerCase() === EMAIL_PREFORMATTED_TAG_NAME

  Array.from(element.attributes).forEach(attribute => {
    paragraph.setAttribute(attribute.name, attribute.value)
  })

  while (element.firstChild) {
    paragraph.appendChild(element.firstChild)
  }

  element.replaceWith(
    withEmailBlockMargin(
      paragraph,
      isPreformatted ? { 'white-space': 'pre-wrap' } : EMPTY_OBJECT
    )
  )
}

function groupEmailInlineRuns(children) {
  return children.reduce((groups, node) => {
    if (isEmailBlockChildNode(node)) {
      return [...groups, { node, type: 'block' }]
    }

    const lastGroup = groups[groups.length - 1]

    if (lastGroup?.type === 'run') {
      lastGroup.nodes.push(node)

      return groups
    }

    return [...groups, { nodes: [node], type: 'run' }]
  }, EMPTY_LIST)
}

// Has block children: they stay, but any inline run between them would glue
// together once the backend unwraps the shell — wrap each run in its own <p>.
function wrapEmailInlineRuns(doc, element) {
  groupEmailInlineRuns(Array.from(element.childNodes))
    .filter(
      group =>
        group.type === 'run' && !group.nodes.every(isWhitespaceOnlyTextNode)
    )
    .forEach(group => {
      const paragraph = doc.createElement('p')

      element.insertBefore(paragraph, group.nodes[0])
      group.nodes.forEach(node => paragraph.appendChild(node))
      withEmailBlockMargin(paragraph)
    })
}

/**
 * Normalize HTML tables for consistent and email-safe rendering.
 *
 * @param {string} html
 * @returns {string}
 */
export function normalizeTablesForEmail(html) {
  if (!html || !isValueStringType(html) || !/<table[\s>]/i.test(html)) {
    return html
  }

  const doc = toDocument(html)

  doc.querySelectorAll('table').forEach(table => {
    normalizeEmailTableElement(table)
    normalizeEmailTableCaption(table)
    normalizeEmailTableCells(table)
  })

  return doc.body.innerHTML
}

/**
 * Turn every block-level tag the backend sanitizer would unwrap into <p>,
 * so none of its content collapses into one glued, unbroken line.
 *
 * @param {string} html
 * @returns {string}
 */
export function normalizeBlocksForEmail(html) {
  if (
    !html ||
    !isValueStringType(html) ||
    !EMAIL_UNWRAPPED_BLOCK_TAGS_PATTERN.test(html)
  ) {
    return html
  }

  const doc = toDocument(html)

  Array.from(doc.querySelectorAll(EMAIL_UNWRAPPED_BLOCK_TAGS_SELECTOR))
    .reverse()
    .forEach(element => {
      if (hasEmailBlockChild(element)) {
        wrapEmailInlineRuns(doc, element)
      } else {
        normalizeEmailLeafBlock(doc, element)
      }
    })

  return doc.body.innerHTML
}
